/**
 * GridView.jsx
 *
 * Componenti per la visualizzazione multi-pagina del catalogo Fricke/Pagination.
 * Dipende da LayoutPreview.jsx per il rendering delle singole celle.
 *
 * Uso:
 *   import { GridView } from './GridView';
 *   import config from './layout_config_sample.json';
 *
 *   // Prepara i placements dal CSV grezzo
 *   const placements = groupPlacements(csvRows);
 *
 *   <GridView
 *     config={config}
 *     placements={placements}
 *     debug={true}
 *   />
 *
 * Struttura placement in input:
 *   { layoutId, imgSize, product }               // layout standard
 *   { layoutId, imgSize, product, tableRows[] }  // T1/T2 (da groupPlacements)
 *   { layoutId, imgSize, products[] }            // bkg2
 *   { layoutId, imgSize, product, cols, rows }   // override esplicito dimensioni
 */

import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { LayoutPreview } from './LayoutPreview';
import ErrorIcon from '@mui/icons-material/Error';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';

// ─── Costanti ────────────────────────────────────────────────────────────────

const GRID_COLS    = 6;
const GRID_ROWS    = 12;   // righe per pagina A4
const PAGE_W_MM    = 175;
const PAGE_H_MM    = 250;
const PAGE_GAP_MM  = 3.5;  // gutter tra colonne e righe (formato A4)

// ─── groupPlacements ──────────────────────────────────────────────────────────

/**
 * Restituisce il maxTableRows più piccolo tra le varianti del layout (worst case).
 * Usato come soglia per decidere se un blocco necessita di misurazione (split).
 */
function getMinMaxTableRows(layoutId, layouts) {
  const layout = layouts?.find(l => l.id === layoutId);
  if (!layout?.variants) return Infinity;
  let minMax = Infinity;
  for (const v of layout.variants) {
    if (!v.tiers?.length) continue;
    // Secondo-ultimo tier: blocchi che superano questa soglia potrebbero non entrare
    // nell'ultimo tier su pagine con lastPageRows ridotte → devono essere misurati.
    const tier = v.tiers.length >= 2
      ? v.tiers[v.tiers.length - 2]
      : v.tiers[v.tiers.length - 1];
    if (tier.maxTableRows > 0) minMax = Math.min(minMax, tier.maxTableRows);
  }
  return isFinite(minMax) ? minMax : Infinity;
}

/**
 * Trasforma un array di righe CSV grezze in `placements` per GridView.
 *
 * Le righe T1/T2 con lo stesso `blockId` vengono aggregate in un unico placement
 * con tutte le righe del blocco in `tableRows`. Se `nLines` supera la capacità
 * conservativa del tier massimo, il placement viene marcato con `needsMeasurement:true`
 * — lo split effettivo avviene client-side dopo la misurazione delle altezze reali.
 *
 * Campi CSV attesi: 'Layout', 'Image Size', 'blockId' (opzionale).
 *
 * @param {object[]} csvRows - righe CSV come oggetti chiave/valore
 * @returns {object[]}       - placements pronti per GridView
 */
export function groupPlacements(csvRows, config) {
  const csvMapping = config?.meta?.csvMapping ?? {};
  const fLayout    = csvMapping['layout']    ?? 'Layout';
  const fImgSize   = csvMapping['imgSize']   ?? 'Image Size';
  const fBlockId   = csvMapping['blockId']   ?? 'blockId';
  const fNLines    = csvMapping['n lines']   ?? 'n lines';

  const groups = [];
  const seen   = new Set();

  for (const row of csvRows) {
    const layoutId = row[fLayout];
    const imgSize  = row[fImgSize];
    const blockId  = row[fBlockId];

    if (blockId) {
      if (!seen.has(blockId)) {
        seen.add(blockId);
        const blockRows = csvRows.filter(r => r[fBlockId] === blockId);

        if (layoutId.startsWith('bkg')) {
          // Layout background multi-slot: products[] per LayoutPreview
          groups.push({ layoutId, imgSize, product: row, products: blockRows });
        } else {
          // Layout tabella (T/T2): raw placement, l'eventuale split viene deciso
          // dopo la misurazione DOM (vedi TableMeasurer + blockSplits in GridView.js).
          const nLines        = parseInt(row[fNLines], 10);
          const effectiveRows = !isNaN(nLines) && nLines > 0 ? nLines : undefined;
          const minMaxTier    = getMinMaxTableRows(layoutId, config?.layouts);
          const needsMeasurement = effectiveRows !== undefined && effectiveRows > minMaxTier;

          groups.push({
            layoutId,
            imgSize,
            product: row,
            tableRows: blockRows,
            effectiveRows,
            blockId,
            needsMeasurement,
          });
        }
      }
      // Righe successive con lo stesso blockId: già incluse, si saltano
    } else {
      groups.push({ layoutId, imgSize, product: row });
    }
  }

  return groups;
}

// ─── Algoritmo di packing ─────────────────────────────────────────────────────

function newGrid(cols, rows) {
  return Array.from({ length: rows }, () => new Array(cols).fill(false));
}

function findFit(grid, colSpan, rowSpan, cols, rows, minRow = 0) {
  for (let row = minRow; row <= rows - rowSpan; row++) {
    for (let col = 0; col <= cols - colSpan; col++) {
      let fits = true;
      outer: for (let r = row; r < row + rowSpan; r++) {
        for (let c = col; c < col + colSpan; c++) {
          if (grid[r][c]) { fits = false; break outer; }
        }
      }
      if (fits) return { col, row };
    }
  }
  return null;
}

function markOccupied(grid, col, row, colSpan, rowSpan) {
  for (let r = row; r < row + rowSpan; r++) {
    for (let c = col; c < col + colSpan; c++) {
      grid[r][c] = true;
    }
  }
}

/**
 * Ricava colSpan e rowSpan per un placement.
 * Priorità: 1) override esplicito nel placement, 2) layout.cols/rows,
 * 3) tier T1/T2 scelto in base a tableRows.length, 4) fallback pagina intera.
 */
function getSpans(layout, placement, tierOverride) {
  // Tier dinamico: override dalla misurazione reale del contenuto tabella
  if (tierOverride) {
    return { colSpan: tierOverride.cols, rowSpan: tierOverride.rows };
  }
  if (placement.cols && placement.rows) {
    return { colSpan: placement.cols, rowSpan: placement.rows };
  }
  if (layout.cols && layout.rows) {
    return { colSpan: layout.cols, rowSpan: layout.rows };
  }
  // Layout tabella (T1/T2): sceglie il tier minimo che contiene tutte le righe
  const variant = layout.variants?.find(v => v.imgSize === placement.imgSize)
                ?? layout.variants?.[0];
  if (variant?.tiers?.length) {
    // effectiveRows (da "n lines" in data integration) ha priorità su tableRows.length
    const nRows = placement.effectiveRows ?? placement.tableRows?.length ?? 0;
    const tier  = nRows > 0
      ? (variant.tiers.find(t => t.maxTableRows >= nRows) ?? variant.tiers[variant.tiers.length - 1])
      : variant.tiers[variant.tiers.length - 1]; // nessun dato: usa il tier massimo
    return { colSpan: tier.cols, rowSpan: tier.rows };
  }
  return { colSpan: GRID_COLS, rowSpan: GRID_ROWS };
}

// ─── Chunking adattivo per blocchi tabella misurati ──────────────────────────

/**
 * Dato un placement misurato + il grid corrente, restituisce { tier, rowsFit, pos }.
 * pos è la posizione validata via findFit — il tier scelto entra sicuramente nel grid.
 *
 * Strategia in ordine di preferenza:
 *   1) Tier PIÙ PICCOLO che contiene TUTTE le righe rimaste E entra nel grid
 *      (ottimale: come InDesign, no spazio sprecato)
 *   2) Chunk parziale col tier PIÙ GRANDE che entra nel grid (per fittare più righe possibile)
 *   3) Fallback: 1 riga con qualsiasi tier che entra (evita che il blocco sparisca)
 *
 * isFirstChunk=true: table area = min(tierCellH - tableZone.y, tableZone.h)
 * isFirstChunk=false: table area = tierCellH - 2*contMargin (continuation senza immagine)
 *
 * Colonne (T2): riempie una colonna, poi la successiva. Righe intere per colonna.
 */
function fitAdaptiveChunk(placement, layout, grid, gridCols, availRows, minRow, rowHeightMm, gapMm, isFirstChunk, contMarginMm) {
  const variant = layout.variants?.find(v => v.imgSize === placement.imgSize) ?? layout.variants?.[0];
  const tableZone = variant?.zones?.find(z => z.type === 'table');
  if (!tableZone || !variant?.tiers?.length) return null;

  const { rowHeights, headerHeight = 0 } = placement;
  if (!rowHeights?.length) return null;

  const columns = tableZone.columns ?? 1;

  // Spazio verticale disponibile per colonna in un dato tier
  function tierAvailablePerCol(tier) {
    if (tier.cols > gridCols) return -1;
    const tierCellH = tier.rows * rowHeightMm + (tier.rows - 1) * gapMm;
    const tableAreaMm = isFirstChunk
      ? Math.min(tierCellH - tableZone.y, tableZone.h)
      : tierCellH - 2 * contMarginMm;
    const available = tableAreaMm - headerHeight;
    return available > 0 ? available : -1;
  }

  // Quante righe totali entrano in un chunk.
  // T1 (1 colonna): fill sequenziale semplice.
  // T2 (2 colonne): fill sequenziale — riempie col1 fino alla capacità, poi col2.
  // Questo replica il comportamento InDesign (frame a 2 colonne, column-fill:auto).
  function rowsFittingInTier(availablePerCol) {
    const avail = availablePerCol;
    const total = rowHeights.length;
    let cum1 = 0, col1 = 0;
    for (let i = 0; i < total; i++) {
      if (cum1 + rowHeights[i] > avail) break;
      cum1 += rowHeights[i];
      col1++;
    }
    if (columns === 1) return col1;
    let cum2 = 0, col2 = 0;
    for (let i = col1; i < total; i++) {
      if (cum2 + rowHeights[i] > avail) break;
      cum2 += rowHeights[i];
      col2++;
    }
    return col1 + col2;
  }

  const smallToLarge = [...variant.tiers].sort((a, b) => a.rows - b.rows);
  const largeToSmall = [...variant.tiers].sort((a, b) => b.rows - a.rows);

  // 1) Per il primo chunk usa il tier più grande (riempie la pagina);
  //    per i chunk di continuazione usa il più piccolo (evita spazio sprecato).
  const strategy1Order = isFirstChunk ? largeToSmall : smallToLarge;
  for (const tier of strategy1Order) {
    const availPerCol = tierAvailablePerCol(tier);
    if (availPerCol < 0) continue;
    if (rowsFittingInTier(availPerCol) !== rowHeights.length) continue;
    const pos = findFit(grid, tier.cols, tier.rows, gridCols, availRows, minRow);
    if (!pos) continue;
    return { tier, rowsFit: rowHeights.length, pos };
  }

  // 2) Chunk parziale col tier più grande che entra
  for (const tier of largeToSmall) {
    const availPerCol = tierAvailablePerCol(tier);
    if (availPerCol < 0) continue;
    const pos = findFit(grid, tier.cols, tier.rows, gridCols, availRows, minRow);
    if (!pos) continue;
    const rf = rowsFittingInTier(availPerCol);
    if (rf > 0) return { tier, rowsFit: rf, pos };
  }

  // 3) Fallback: 1 riga con qualsiasi tier che entra (evita sparizione del blocco)
  for (const tier of largeToSmall) {
    const pos = findFit(grid, tier.cols, tier.rows, gridCols, availRows, minRow);
    if (!pos) continue;
    return { tier, rowsFit: 1, pos };
  }

  return null;
}

// ─── Hook usePacking ──────────────────────────────────────────────────────────

/**
 * Singola passata di bin-packing.
 * Se lastPage >= 1, le pagine con numero >= lastPage usano lastPageRows righe disponibili.
 * Se firstPageStartRow > 0, le righe 0..firstPageStartRow-1 di pagina 1 sono pre-occupate.
 */
function packOnce(placements, config, gridCols, gridRows, lastPageRows, lastPage, tierOverrides, firstPageStartRow = 0) {
  const pages = [];
  let grid = newGrid(gridCols, gridRows);
  let currentPage = [];
  let pageNum = 1;

  // Pre-occupa le righe riservate alla grafica di copertina (es. righe 0-7)
  if (firstPageStartRow > 0) {
    for (let r = 0; r < firstPageStartRow; r++)
      for (let c = 0; c < gridCols; c++)
        grid[r][c] = true;
  }

  let minRow = firstPageStartRow > 0 ? firstPageStartRow : 0;

  function avRows() {
    return (lastPage >= 1 && pageNum >= lastPage) ? lastPageRows : gridRows;
  }

  // Parametri per il chunking adattivo (in mm)
  const pageHMm     = config?.meta?.pageHeightMm ?? PAGE_H_MM;
  const gapMm       = config?.meta?.gapMm ?? PAGE_GAP_MM;
  const rowHeightMm = (pageHMm - (gridRows - 1) * gapMm) / gridRows;

  function openNewPage() {
    if (currentPage.length > 0) pages.push(currentPage);
    grid = newGrid(gridCols, gridRows);
    currentPage = [];
    minRow = 0;
    pageNum++;
  }

  for (let idx = 0; idx < placements.length; idx++) {
    const placement = placements[idx];
    const layout = config.layouts?.find(l => l.id === placement.layoutId);
    if (!layout) {
      console.warn(`GridView: layout "${placement.layoutId}" non trovato nel config`);
      continue;
    }

    // ── Placement con reflow adattivo (T/T2 misurati) ────────────────────────
    // Il chunking viene deciso qui in base allo spazio disponibile sulla pagina,
    // producendo N chunk placements virtuali con lo stesso originalIndex.
    if (placement.measured && placement.rowHeights && placement.rowHeights.length > 0) {
      const variant   = layout.variants?.find(v => v.imgSize === placement.imgSize) ?? layout.variants?.[0];
      const imageZone = variant?.zones?.find(z => z.type === 'image');
      const contMarginMm = imageZone?.y ?? 5;

      let remainingHeights = [...placement.rowHeights];
      let remainingRows    = [...placement.tableRows];
      const producedChunks = [];
      let chunkIdx = 0;
      let safety = placements.length * 3 + 10; // guard contro loop infinito

      while (remainingHeights.length > 0 && safety-- > 0) {
        const isFirstChunk = chunkIdx === 0;

        // fitAdaptiveChunk fa findFit internamente per ogni tier candidato.
        // Sceglie il tier più efficiente che entra fisicamente nello spazio residuo.
        const attempt = fitAdaptiveChunk(
          { ...placement, rowHeights: remainingHeights, tableRows: remainingRows },
          layout, grid, gridCols, avRows(), minRow, rowHeightMm, gapMm,
          isFirstChunk, contMarginMm
        );

        if (!attempt) {
          if (currentPage.length === 0) {
            console.warn(`GridView: blocco "${placement.blockId}" non entra in alcun tier`);
            break;
          }
          openNewPage();
          continue;
        }

        const { tier, rowsFit, pos } = attempt;

        markOccupied(grid, pos.col, pos.row, tier.cols, tier.rows);
        minRow = pos.row;

        const chunkRows    = remainingRows.slice(0, rowsFit);
        const chunkHeights = remainingHeights.slice(0, rowsFit);
        // effectiveRows proporzionale al chunk: evita che il percorso normale (fallback)
        // usi l'effectiveRows del blocco originale e scelga un tier troppo grande.
        const origTotal    = placement.tableRows?.length || 1;
        const origEff      = placement.effectiveRows ?? origTotal;
        const chunkEff     = Math.max(1, Math.round(chunkRows.length * origEff / origTotal));
        const chunkPlacement = {
          ...placement,
          product:        chunkRows[0],
          tableRows:      chunkRows,
          rowHeights:     chunkHeights,
          effectiveRows:  chunkEff,
          chunkIndex:     chunkIdx,
          // totalChunks aggiornato in un secondo passaggio
          col:            pos.col + 1,
          row:            pos.row + 1,
          colSpan:        tier.cols,
          rowSpan:        tier.rows,
          originalIndex:  idx,
        };
        currentPage.push(chunkPlacement);
        producedChunks.push(chunkPlacement);

        remainingHeights = remainingHeights.slice(rowsFit);
        remainingRows    = remainingRows.slice(rowsFit);
        chunkIdx++;
      }

      // Aggiorna totalChunks su tutti i chunk prodotti (in-place; sono già dentro currentPage/pages)
      const totalChunks = producedChunks.length;
      producedChunks.forEach(c => { c.totalChunks = totalChunks; });

      continue;
    }

    // ── Placement normale ─────────────────────────────────────────────────────
    const tierOverride = tierOverrides?.get?.(idx);
    let { colSpan, rowSpan } = getSpans(layout, placement, tierOverride);
    let avR = avRows();
    let pos = findFit(grid, colSpan, rowSpan, gridCols, avR, minRow);

    if (!pos) {
      openNewPage();
      avR = avRows();
      pos = findFit(grid, colSpan, rowSpan, gridCols, avR, minRow);

      // Se il tier scelto non entra nemmeno nella nuova pagina (es. banner lastPageRows),
      // prova tier più piccoli dello stesso layout.
      if (!pos && layout.variants) {
        const variant = layout.variants.find(v => v.imgSize === placement.imgSize) ?? layout.variants[0];
        if (variant?.tiers) {
          // Discendente: prova prima il tier più grande che entra → massimizza spazio
          const sorted = [...variant.tiers].sort((a, b) => b.rows - a.rows);
          for (const t of sorted) {
            if (t.rows > avR || t.cols > gridCols) continue;
            const p2 = findFit(grid, t.cols, t.rows, gridCols, avR, minRow);
            if (p2) {
              pos = p2; colSpan = t.cols; rowSpan = t.rows; break;
            }
          }
        }
      }

      if (!pos) {
        console.warn(`GridView: layout "${placement.layoutId}" (${colSpan}×${rowSpan}) non entra in una pagina ${gridCols}×${avR}`);
        continue;
      }
    }

    markOccupied(grid, pos.col, pos.row, colSpan, rowSpan);
    minRow = pos.row;
    currentPage.push({
      ...placement,
      col:          pos.col + 1,  // CSS Grid è 1-indexed
      row:          pos.row + 1,
      colSpan,
      rowSpan,
      originalIndex: idx,
    });
  }

  if (currentPage.length > 0) pages.push(currentPage);
  return pages;
}

/**
 * Distribuisce i placements in pagine usando un algoritmo first-fit top-left.
 * L'ultima pagina ha `lastPageRows` righe disponibili (default = gridRows, nessuna restrizione).
 * Quando lastPageRows < gridRows, converge iterativamente in max 5 passate.
 * Se firstPageStartRow > 0, la pagina 1 ha le prime `firstPageStartRow` righe pre-occupate
 * (riservate alla grafica di copertina nel layout Highlights).
 *
 * @param {object[]} placements       - array di { layoutId, imgSize, product|products, cols?, rows? }
 * @param {object}   config           - JSON completo
 * @param {number}   gridCols         - colonne per pagina (default 6)
 * @param {number}   gridRows         - righe per pagina (default 12)
 * @param {number}   lastPageRows     - righe disponibili sull'ultima pagina (default = gridRows)
 * @param {Map}      tierOverrides    - override tier dinamici per layout tabella
 * @param {number}   firstPageStartRow - righe pre-occupate in cima a pagina 1 (default 0)
 * @returns {object[][]}              - pages[ page[ { ...placement, col, row, colSpan, rowSpan } ] ]
 */
export function usePacking(placements, config, gridCols = GRID_COLS, gridRows = GRID_ROWS, lastPageRows = gridRows, tierOverrides = null, firstPageStartRow = 0) {
  return useMemo(() => {
    if (!placements?.length || !config) return [];

    if (lastPageRows >= gridRows) {
      return packOnce(placements, config, gridCols, gridRows, gridRows, -1, tierOverrides, firstPageStartRow);
    }

    const N = packOnce(placements, config, gridCols, gridRows, gridRows, -1, tierOverrides, firstPageStartRow).length;
    return packOnce(placements, config, gridCols, gridRows, lastPageRows, N, tierOverrides, firstPageStartRow);
  }, [placements, config, gridCols, gridRows, lastPageRows, tierOverrides, firstPageStartRow]);
}

const PRIMARY_COLOR = '#3397E2';

const SUGGESTION_COLORS = {
  overflow:  { main: '#f59e0b' },
  imageFit:  { main: '#5C6BC0' },
  spaceFill: { main: '#26A69A' },
};

const ADVISOR_ICONS = {
  overflow:  ErrorIcon,
  imageFit:  FitScreenIcon,
  spaceFill: AutoAwesomeMosaicIcon,
};

// ─── ItemBadge ───────────────────────────────────────────────────────────────

/**
 * Stacked badges: up to 3 (overflow orange, imageFit violet, spaceFill teal).
 * Each badge is a small circle with icon; click opens popover with detail + accept/reject.
 */
function ItemBadges({ hasOverflow, activeAdvisors, imageFitSuggestion, spaceFillSuggestion, originalIndex, onAcceptSuggestion, onRejectSuggestion }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverType, setPopoverType] = useState(null);

  const badges = [];
  if (hasOverflow) badges.push({ type: 'overflow', color: SUGGESTION_COLORS.overflow.main, Icon: ADVISOR_ICONS.overflow });
  if (activeAdvisors?.has('imageFit') && imageFitSuggestion?.status === 'pending')
    badges.push({ type: 'imageFit', color: SUGGESTION_COLORS.imageFit.main, Icon: ADVISOR_ICONS.imageFit, suggestion: imageFitSuggestion });
  if (activeAdvisors?.has('spaceFill') && spaceFillSuggestion?.status === 'pending')
    badges.push({ type: 'spaceFill', color: SUGGESTION_COLORS.spaceFill.main, Icon: ADVISOR_ICONS.spaceFill, suggestion: spaceFillSuggestion });

  const overflowBadge = badges.find(b => b.type === 'overflow');
  const suggestionBadges = badges.filter(b => b.type !== 'overflow');

  if (!overflowBadge && suggestionBadges.length === 0) return null;

  const handleClick = (e, type) => { e.stopPropagation(); setAnchorEl(e.currentTarget); setPopoverType(type); };
  const handleClose = (e) => { e?.stopPropagation?.(); setAnchorEl(null); setPopoverType(null); };

  const activeBadge = badges.find(b => b.type === popoverType);
  let popoverContent = null;
  if (activeBadge?.type === 'overflow') {
    popoverContent = <div style={{ padding: '8px 12px', fontSize: 11, color: '#b45309' }} onClick={e => e.stopPropagation()}>Text overflow</div>;
  } else if (activeBadge?.suggestion) {
    const s = activeBadge.suggestion;
    const detail = s.field === 'layout'
      ? `Layout: ${s.original} → ${s.suggested}${s.suggestedImgSize ? `\nImg Size: ${s.originalImgSize} → ${s.suggestedImgSize}` : ''}`
      : `Img Size: ${s.original} → ${s.suggested}`;
    popoverContent = (
      <div style={{ padding: '10px 14px', maxWidth: 220 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 11, color: '#555', whiteSpace: 'pre-line', marginBottom: 8 }}>{detail}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="small" variant="contained"
            onClick={(e) => { e.stopPropagation(); onAcceptSuggestion?.(activeBadge.type, originalIndex); handleClose(e); }}
            sx={{ fontSize: 11, textTransform: 'none', minWidth: 0, px: 1.5, py: 0.3, background: activeBadge.color, '&:hover': { background: activeBadge.color, filter: 'brightness(0.9)' } }}
          >Accept</Button>
          <Button size="small" variant="outlined"
            onClick={(e) => { e.stopPropagation(); onRejectSuggestion?.(activeBadge.type, originalIndex); handleClose(e); }}
            sx={{ fontSize: 11, textTransform: 'none', minWidth: 0, px: 1.5, py: 0.3, color: '#999', borderColor: '#ddd' }}
          >Reject</Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Overflow badge: top-right */}
      {overflowBadge && (
        <div style={{ position: 'absolute', top: 2, right: 2, zIndex: 20, pointerEvents: 'auto' }}>
          <div onClick={e => handleClick(e, 'overflow')} style={{ cursor: 'pointer', lineHeight: 0 }}>
            <overflowBadge.Icon sx={{ fontSize: 22, color: overflowBadge.color, background: '#fff', borderRadius: '50%', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))' }} />
          </div>
        </div>
      )}
      {/* Suggestion badges: bottom-right */}
      {suggestionBadges.length > 0 && (
        <div style={{ position: 'absolute', bottom: 2, right: 2, display: 'flex', flexDirection: 'column', gap: 2, zIndex: 20, pointerEvents: 'auto' }}>
          {suggestionBadges.map(b => (
            <div key={b.type} onClick={e => handleClick(e, b.type)}
              style={{
                width: 20, height: 20, borderRadius: '50%', background: b.color,
                boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <b.Icon sx={{ fontSize: 14, color: '#fff' }} />
            </div>
          ))}
        </div>
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={e => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' } } }}
      >
        {popoverContent}
      </Popover>
    </>
  );
}

// ─── GridItem ─────────────────────────────────────────────────────────────────

/**
 * Singola cella del grid: posizionata tramite CSS Grid, contiene LayoutPreview.
 *
 * Per i layout T1/T2 calcola `cellOverride` (altezza reale in mm del tier scelto)
 * e lo passa a LayoutPreview così che il componente non usi la cell.h dell'IDMS
 * (che è sempre 250mm, la pagina intera) ma l'altezza effettiva nella griglia.
 */
function GridItem({ config, placement, pageHeightMm, gridRows, gapMm, colWidthPx, gapPx, debug, hoveredIndex, selectedIndex, onItemClick, onOverflow, onTextMeasure, overflowSet, onTierChange, showImages, activeAdvisors, imageFitSuggestions, spaceFillSuggestions, onAcceptSuggestion, onRejectSuggestion }) {
  const { col, row, colSpan, rowSpan, layoutId, imgSize, product, products, tableRows, rowHeights, headerHeight, originalIndex, chunkIndex = 0, totalChunks = 1 } = placement;
  const itemWidthPx = colWidthPx * colSpan + gapPx * (colSpan - 1);

  const productArray = products ?? (product ? [product] : []);

  // cellOverride: solo per layout tabella, per correggere il mismatch cell.h vs tier height
  const layout      = config?.layouts?.find(l => l.id === layoutId);
  const isTable     = layout?.type === 'table';
  const rowHeightMm = (pageHeightMm - (gridRows - 1) * gapMm) / gridRows;
  const cellOverride = isTable
    ? rowSpan * rowHeightMm + (rowSpan - 1) * gapMm   // altezza reale del tier in mm (include i gutter interni)
    : undefined;

  const isHovered  = hoveredIndex !== null && hoveredIndex === originalIndex;
  const isSelected = selectedIndex !== null && selectedIndex === originalIndex;
  const hasOverflow = overflowSet != null && (overflowSet.has(originalIndex) || [...overflowSet].some(k => typeof k === 'string' && k.startsWith(`${originalIndex}:`)));

  const stableOnOverflow = useCallback((slotIdx) => {
    onOverflow?.(originalIndex, slotIdx)
  }, [onOverflow, originalIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const stableOnTextMeasure = useCallback((scrollHeight, slotIdx) => {
    onTextMeasure?.(originalIndex, scrollHeight, slotIdx)
  }, [onTextMeasure, originalIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tier dinamico: quando TableZone misura il contenuto reale, trova il tier corretto
  // e notifica il parent se diverso dall'attuale rowSpan.
  const handleTableMeasure = useCallback((neededCellHeightMm) => {
    if (!isTable || !layout || !onTierChange) return;
    const variant = layout.variants?.find(v => v.imgSize === imgSize) ?? layout.variants?.[0];
    if (!variant?.tiers?.length) return;

    // Trova il tier più piccolo che contiene il contenuto
    const tier = variant.tiers.find(t => {
      const tierHeightMm = t.rows * rowHeightMm + (t.rows - 1) * gapMm;
      return tierHeightMm >= neededCellHeightMm;
    }) ?? variant.tiers[variant.tiers.length - 1]; // fallback: tier massimo

    onTierChange(originalIndex, tier.rows, tier.cols);
  }, [isTable, layout, imgSize, rowHeightMm, gapMm, originalIndex, onTierChange]);

  const isBkg = layoutId.startsWith('bkg');
  const nSlots = layout?.variants?.[0]?.slots?.length || (layout?.slotOffset ? 1 : 0);

  const renderSlotOverlay = isBkg && nSlots > 0 ? (si) => {
    const slotKey = nSlots > 1 ? `${originalIndex}:${si}` : originalIndex;
    const slotOverflow = overflowSet != null && overflowSet.has(slotKey);
    const slotIfSugg = imageFitSuggestions?.get?.(slotKey);
    return (
      <ItemBadges
        hasOverflow={slotOverflow}
        activeAdvisors={activeAdvisors}
        imageFitSuggestion={slotIfSugg}
        spaceFillSuggestion={null}
        originalIndex={slotKey}
        onAcceptSuggestion={onAcceptSuggestion}
        onRejectSuggestion={onRejectSuggestion}
      />
    );
  } : undefined;

  return (
    <div
      onClick={() => onItemClick?.(originalIndex)}
      style={{
        gridColumn: `${col} / span ${colSpan}`,
        gridRow:    `${row} / span ${rowSpan}`,
        overflow:   'hidden',
        minWidth:   0,
        minHeight:  0,
        position:   'relative',
        cursor:     onItemClick ? 'pointer' : 'default',
      }}
    >
      <LayoutPreview
        key={`${layoutId}-${imgSize}`}
        config={config}
        layoutId={layoutId}
        imgSize={imgSize}
        products={productArray}
        tableRows={tableRows ?? []}
        rowHeights={rowHeights}
        rowsHeaderHeight={headerHeight}
        chunkIndex={chunkIndex}
        totalChunks={totalChunks}
        cellOverride={cellOverride}
        precomputedWidth={itemWidthPx}
        debug={debug}
        onOverflow={stableOnOverflow}
        onTextMeasure={stableOnTextMeasure}
        onTableMeasure={isTable && onTierChange && (totalChunks <= 1 || chunkIndex === totalChunks - 1) ? handleTableMeasure : undefined}
        showImages={showImages}
        renderSlotOverlay={renderSlotOverlay}
      />
      {(isHovered || isSelected) && (
        <div style={{
          position:      'absolute',
          inset:         0,
          boxShadow:     `inset 0 0 0 ${isSelected ? '2.5px' : '1.5px'} ${PRIMARY_COLOR}`,
          pointerEvents: 'none',
          zIndex:        11,
        }} />
      )}
      {!isBkg && (
        <ItemBadges
          hasOverflow={hasOverflow}
          activeAdvisors={activeAdvisors}
          imageFitSuggestion={imageFitSuggestions?.get?.(originalIndex)}
          spaceFillSuggestion={spaceFillSuggestions?.get?.(originalIndex)}
          originalIndex={originalIndex}
          onAcceptSuggestion={onAcceptSuggestion}
          onRejectSuggestion={onRejectSuggestion}
        />
      )}
    </div>
  );
}

// ─── GridPage ─────────────────────────────────────────────────────────────────

/**
 * Una singola pagina del catalogo.
 * Adattiva: occupa il 100% del contenitore, altezza proporzionale al formato A4.
 *
 * @param {object}   config        - JSON completo
 * @param {object[]} page          - array di placements posizionati (da usePacking)
 * @param {number}   gridCols      - colonne (default 6)
 * @param {number}   gridRows      - righe (default 12)
 * @param {number}   pageWidthMm   - larghezza pagina in mm (default 175)
 * @param {number}   pageHeightMm  - altezza pagina in mm (default 250)
 * @param {boolean}  debug         - bordi di debug
 */
export function GridPage({
  config,
  page,
  gridCols     = GRID_COLS,
  gridRows     = GRID_ROWS,
  pageWidthMm  = PAGE_W_MM,
  pageHeightMm = PAGE_H_MM,
  gapMm        = PAGE_GAP_MM,
  debug        = true,
  hoveredIndex  = null,
  selectedIndex = null,
  onItemClick   = null,
  isFinalPage   = false,
  isCoverPage   = false,
  onOverflow    = null,
  onTextMeasure = null,
  overflowSet   = null,

  onTierChange  = null,
  showImages    = false,
  activeAdvisors       = new Set(),
  imageFitSuggestions  = new Map(),
  spaceFillSuggestions = new Map(),
  onAcceptSuggestion   = null,
  onRejectSuggestion   = null,
}) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(entries => {
      setContainerWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const margin        = 12;  // px di margine attorno alla pagina (colore: background su outer div)
  const effectiveWidth = containerWidth;  // contentRect esclude già il padding CSS
  const scale         = effectiveWidth > 0 ? effectiveWidth / pageWidthMm : 0;
  const pageHeightPx  = pageHeightMm * scale;
  const gapPx         = gapMm * scale;
  const colWidthPx    = ((pageWidthMm  - (gridCols - 1) * gapMm) / gridCols) * scale;
  const rowHeightPx   = ((pageHeightMm - (gridRows - 1) * gapMm) / gridRows) * scale;

  return (
    <div ref={containerRef} style={{
      width:      '100%',
      background: '#ffffff',
      padding:    margin,
      boxSizing:  'border-box',
      ...(debug ? {
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
      } : {}),
    }}>
      {scale > 0 && (
        <div
          style={{
            display:             'grid',
            gridTemplateColumns: `repeat(${gridCols}, ${colWidthPx}px)`,
            gridTemplateRows:    `repeat(${gridRows}, ${rowHeightPx}px)`,
            gap:                 gapPx,
            width:               effectiveWidth,
            height:              pageHeightPx,
            background:          '#ffffff',
            flexShrink:          0,
          }}
        >
          {isCoverPage && page.length === 0 && (
            <div style={{
              gridColumn:     `1 / span ${gridCols}`,
              gridRow:        `1 / span ${gridRows}`,
              background:     '#efefef',
              border:         '1px dashed #c0c0c0',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              fontSize:       Math.round(rowHeightPx * 0.35),
              color:          '#aaaaaa',
              fontFamily:     'Roboto, sans-serif',
              letterSpacing:  '0.1em',
              userSelect:     'none',
              overflow:       'hidden',
            }}>
              COVER
            </div>
          )}
          {isCoverPage && page.length > 0 && (
            <div style={{
              gridColumn:     `1 / span ${gridCols}`,
              gridRow:        `1 / span 8`,
              background:     '#efefef',
              border:         '1px dashed #c0c0c0',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              fontSize:       Math.round(rowHeightPx * 0.35),
              color:          '#aaaaaa',
              fontFamily:     'Roboto, sans-serif',
              letterSpacing:  '0.1em',
              userSelect:     'none',
              overflow:       'hidden',
            }}>
              COVER
            </div>
          )}
          {(!isCoverPage || page.length > 0) && page.map((placement, i) => (
            <GridItem
              key={i}
              config={config}
              placement={placement}
              pageHeightMm={pageHeightMm}
              gridRows={gridRows}
              gapMm={gapMm}
              colWidthPx={colWidthPx}
              gapPx={gapPx}
              debug={debug}
              hoveredIndex={hoveredIndex}
              selectedIndex={selectedIndex}
              onItemClick={onItemClick}
              onOverflow={onOverflow}
              onTextMeasure={onTextMeasure}
              overflowSet={overflowSet}

              onTierChange={onTierChange}
              showImages={showImages}
              activeAdvisors={activeAdvisors}
              imageFitSuggestions={imageFitSuggestions}
              spaceFillSuggestions={spaceFillSuggestions}
              onAcceptSuggestion={onAcceptSuggestion}
              onRejectSuggestion={onRejectSuggestion}
            />
          ))}
          {isFinalPage && (
            <div style={{
              gridColumn:     `1 / span ${gridCols}`,
              gridRow:        `${gridRows} / span 1`,
              background:     '#efefef',
              border:         '1px dashed #c0c0c0',
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              fontSize:       Math.round(rowHeightPx * 0.35),
              color:          '#aaaaaa',
              fontFamily:     'Roboto, sans-serif',
              letterSpacing:  '0.1em',
              userSelect:     'none',
              overflow:       'hidden',
            }}>
              BANNER
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── GridView ─────────────────────────────────────────────────────────────────

/**
 * Componente preview pagina singola (layout v3).
 * Renderizza solo la pagina `currentPage` dell'array pages calcolato da usePacking.
 * La navigazione tra pagine è gestita dal componente padre tramite `currentPage`.
 *
 * @param {object}   config        - JSON completo (layout_config_sample.json)
 * @param {object[]} placements    - lista prodotti con layoutId, imgSize, product|products
 * @param {number}   currentPage   - pagina corrente da visualizzare (1-based, default 1)
 * @param {number}   selectedIndex - originalIndex dell'item selezionato (null = nessuno)
 * @param {function} onItemClick   - callback(originalIndex) al click su un item
 * @param {number}   gridCols      - colonne per pagina (default 6)
 * @param {number}   gridRows      - righe per pagina (default 12)
 * @param {number}   pageWidthMm   - larghezza pagina in mm (default 175)
 * @param {number}   pageHeightMm  - altezza pagina in mm (default 250)
 * @param {boolean}  debug         - bordi di debug
 */
export function GridView({
  config,
  placements    = [],
  currentPage   = 1,
  selectedIndex = null,
  onItemClick   = null,
  gridCols      = GRID_COLS,
  gridRows      = GRID_ROWS,
  pageWidthMm   = PAGE_W_MM,
  pageHeightMm  = PAGE_H_MM,
  gapMm         = PAGE_GAP_MM,
  debug         = true,
  hoveredIndex  = null,
  lastPageRows  = GRID_ROWS,
}) {
  const pages = usePacking(placements, config, gridCols, gridRows, lastPageRows);

  if (!pages.length) {
    return (
      <div style={{ padding: 16, color: '#6b7280', fontSize: 14 }}>
        Nessun prodotto da visualizzare.
      </div>
    );
  }

  const page = pages[currentPage - 1] ?? pages[0];

  return (
    <GridPage
      config={config}
      page={page}
      gridCols={gridCols}
      gridRows={gridRows}
      pageWidthMm={pageWidthMm}
      pageHeightMm={pageHeightMm}
      gapMm={gapMm}
      debug={debug}
      hoveredIndex={hoveredIndex}
      selectedIndex={selectedIndex}
      onItemClick={onItemClick}
      isFinalPage={currentPage === pages.length}
    />
  );
}
